import React, { useEffect, useState } from "react";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import { useSelector } from "react-redux";
import { Card, Col, Row, Typography, Dropdown, Menu, Empty, Tabs } from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ProjectOutlined,
  DownOutlined,
  WarningOutlined,
  BugOutlined,
} from "@ant-design/icons";
import StatCard from "./shared/StatCard";
import { statusTone, GenericStatusPill } from "./shared/statusColors";
import "./shared/project.css";
import "./projects/hub/hub.css";
import PlannedVsDeviationChart from "./projectDashboard/plannedvsdeviated";
import OverdueTasksBarChart from "./projectDashboard/OverdueTasksBarChart";
import ProjectCompletionDonutChart from "./projectDashboard/ProjectCompletionDonutChart";
import TaskProgressBarChart from "./projectDashboard/TaskProgressBarChart";
import TaskPlannedVsActualChart from "./projectDashboard/TaskPlannedVsActualChart";
import TeamStatusTable from "./projectDashboard/TeamStatusTable";
import IssueTeamStatusTable from "./projectDashboard/IssueTeamStatusTable";
import OverdueWorkItems from "./projectDashboard/OverdueWorkItems";
import IssueStatusChart from "./projectDashboard/IssueStatusChart";
import WeeklyDigestChart from "./projectDashboard/WeeklyDigestChart";
import TopGoGetters from "./projectDashboard/TopGoGetters";
import TopIssueFixers from "./projectDashboard/TopIssueFixers";
import PhaseStatusChart from "./projectDashboard/PhaseStatus";
import { useLocation, useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import { updateMenuData } from "../../store/slices/misc/menuData";
import TopMenu from "../../route/topMenu";
import { dashboardPMSApis } from "../../api/DashboardPMS";
import { ApiResponse } from "../../api/master/axiosIndex";
import dayjs from "dayjs";
import { useWbsName } from "./shared/useWbsCodes";

const { Text, Title } = Typography;

const ProjectScheduleDashboard = () => {
  const dispatch = useDispatch();
  const wbsName = useWbsName();
  const location = useLocation();
  const navigate = useNavigate();
  const { id: projectId, projectName, extra: extraData } = location.state || {};

  const goToTasksWithStatusFilter = (rawLabel: string) => {
    if (!projectId) return;
    // A few dashboard labels don't match the backend status enum verbatim;
    // map the well-known mismatches so the filter actually applies.
    const labelToStatus: Record<string, string> = {
      "Under Quality Check": "UnderQC",
      UnderQualityChecks: "UnderQC",
      "Yet to Start": "Open",
      OnHold: "OnHold",
      "On Hold": "OnHold",
      InProgress: "InProgress",
      OnTrack: "OnTrack",
    };
    const filterStatus = labelToStatus[rawLabel] || rawLabel;
    navigate(`/pms/project/tasks/${projectId}`, {
      state: {
        ...(location.state || {}),
        id: projectId,
        projectName,
        filterStatus,
        filterStatusLabel: rawLabel,
        fromDashboard: true,
      },
    });
  };
  const { data: currentUser } = useSelector((state: any) => state.currentUser);

  const [activeTab, setActiveTab] = useState("overview");

  // Loading states
  const [loadingDeviationChart, setLoadingDeviationChart] = useState(true);
  const [loadingOverdueChart, setLoadingOverdueChart] = useState(true);
  const [loadingCompletionChart, setLoadingCompletionChart] = useState(true);
  const [loadingPlannedActual, setLoadingPlannedActual] = useState(true);
  const [loadingIssueStatus, setLoadingIssueStatus] = useState(true);
  const [loadingGoGetters, setLoadingGoGetters] = useState(true);
  const [loadingTaskProgressChart, setLoadingTaskProgressChart] = useState(true);
  const [loadingIssueFixers, setLoadingIssueFixers] = useState(true);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingPhaseStatus, setLoadingPhaseStatus] = useState(true);

  // Data states
  const [plannedDeviationData, setPlannedDeviationData] = useState<any>(null);
  const [overdueChartData, setOverdueChartData] = useState<any>(null);
  const [completionChartData, setCompletionChartData] = useState<any>(null);
  const [taskPlannedActualData, setTaskPlannedActualData] = useState<any>(null);
  const [issueStatusData, setIssueStatusData] = useState<any>(null);
  const [goGettersData, setGoGettersData] = useState<any>(null);
  const [taskProgressChartData, setTaskProgressChartData] = useState<any>(null);
  const [issueFixersData, setIssueFixersData] = useState<any>(null);
  const [summaryData, setSummaryData] = useState<any>([]);
  const [phaseStatusData, setPhaseStatusData] = useState<any>(null);
  const [dropdownData, setDropdownData] = useState<any[]>([]);
  const [selectedPhase, setSelectedPhase] = useState<any>(null);

  // KPI calculated values
  const [kpiData, setKpiData] = useState({
    totalTasks: 0,
    completed: 0,
    inProgress: 0,
    overdue: 0,
    issues: 0,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    dispatch(
      updateMenuData(
        TopMenu?.projectNav?.map((item: any) => ({
          ...item,
          linkState: location.state,
        }))
      )
    );
  }, [location]);

  // Force charts to resize when tab changes
  useEffect(() => {
    // Dispatch resize events at different intervals to ensure charts resize properly
    const timers = [50, 150, 300].map((delay) =>
      setTimeout(() => {
        window.dispatchEvent(new Event("resize"));
      }, delay)
    );
    return () => timers.forEach((timer) => clearTimeout(timer));
  }, [activeTab]);

  // Calculate KPI data from summary
  useEffect(() => {
    if (summaryData && summaryData.length > 0) {
      const countOf = (label: string) =>
        summaryData.find((item: any) => item.label === label)?.count || 0;

      // Total Tasks = Open + InProgress + OnHold + UnderQualityChecks + Closed
      const total =
        countOf("Open") +
        countOf("InProgress") +
        countOf("OnHold") +
        countOf("UnderQualityChecks") +
        countOf("Closed");

      const completed = countOf("Closed");
      const inProgress = countOf("InProgress");
      const overdue = countOf("OverdueTasks");
      const issuesFromSummary = countOf("OpenIssues") + countOf("ResolvedIssues");

      setKpiData({
        totalTasks: total,
        completed,
        inProgress,
        overdue,
        issues:
          issuesFromSummary ||
          issueStatusData?.reduce(
            (acc: number, item: any) => acc + (item.count || 0),
            0,
          ) ||
          0,
      });
    }
  }, [summaryData, issueStatusData]);

  // API calls
  const fetchPlannedDeviationData = async () => {
    if (!projectId) {
      setLoadingDeviationChart(false);
      return;
    }
    try {
      const apiResponse: ApiResponse = await dashboardPMSApis.fetchPlannedVsDeviatedTasks(
        projectId,
        "plannedVsDeviatedTasks",
        dayjs(new Date()).format("YYYY-MM-DD")
      );
      const { data, status } = apiResponse;
      if (status && data) {
        setPlannedDeviationData({
          months: data.month || data.months || [],
          planned: data.planned || [],
          deviation: data.deviation || data.deviatedTasks || [],
        });
      }
      setLoadingDeviationChart(false);
    } catch (error) {
      console.log(error);
      setLoadingDeviationChart(false);
    }
  };

  const fetchProjectCompletionStatus = async () => {
    if (!projectId) {
      setLoadingCompletionChart(false);
      return;
    }
    try {
      const apiResponse: ApiResponse = await dashboardPMSApis.fetchProjectCompletionStatus(
        projectId,
        "projectCompletionStatus"
      );
      const { data, status } = apiResponse;
      if (status && data) {
        setCompletionChartData({
          completedPercent: Number(data.closedePCR) || 0,
          pendingPercent: Number(data.pendingTasksPCR) || 0,
          completedCount: Number(data.closedTaskCount) || 0,
          pendingCount: Number(data.pendingTaskCount) || 0,
        });
      }
      setLoadingCompletionChart(false);
    } catch (error) {
      console.log(error);
      setLoadingCompletionChart(false);
    }
  };

  const fetchOverDueTaskOnGoing = async () => {
    try {
      const apiResponse: ApiResponse = await dashboardPMSApis.fetchOverDueTaskOnGoing(
        projectId,
        "overDueTaskOnGoing"
      );
      const { data, status } = apiResponse;
      if (status && data) {
        setOverdueChartData(data);
      }
      setLoadingOverdueChart(false);
    } catch (error) {
      console.log(error);
      setLoadingOverdueChart(false);
    }
  };

  const fetchTaskProgress = async () => {
    try {
      const apiResponse: ApiResponse = await dashboardPMSApis.fetchTaskProgress(
        projectId,
        "taskProgress"
      );
      const { data, status } = apiResponse;
      if (status && data) {
        setTaskProgressChartData(data);
      }
      setLoadingTaskProgressChart(false);
    } catch (error) {
      console.log(error);
      setLoadingTaskProgressChart(false);
    }
  };

  const fetchPlannedVsActualDashBoard = async () => {
    try {
      const apiResponse: ApiResponse = await dashboardPMSApis.fetchPlannedVsActualDashBoard(
        projectId,
        "plannedVsActual"
      );
      const { data, status } = apiResponse;
      if (status && data) {
        setTaskPlannedActualData({
          planned: data.totalPlannedHours,
          actual: data.totalActualHours,
        });
      }
      setLoadingPlannedActual(false);
    } catch (error) {
      console.log(error);
      setLoadingPlannedActual(false);
    }
  };

  const fetchIssueStatusDashBoard = async () => {
    try {
      const apiResponse: ApiResponse = await dashboardPMSApis.fetchIssueStatusDashBoard(
        projectId,
        "issueStatus"
      );
      const { data, status } = apiResponse;
      if (status && data) {
        setIssueStatusData(data);
      }
      setLoadingIssueStatus(false);
    } catch (error) {
      console.log(error);
      setLoadingIssueStatus(false);
    }
  };

  const fetchTopFiveGoGettersOrAssgigned = async () => {
    try {
      const apiResponse: ApiResponse = await dashboardPMSApis.fetchTopFiveGoGettersOrAssgigned(
        projectId,
        "topFiveGoGettersOrAssgigned"
      );
      const { data, status } = apiResponse;
      if (status && data) {
        setGoGettersData(data);
      }
      setLoadingGoGetters(false);
    } catch (error) {
      console.log(error);
      setLoadingGoGetters(false);
    }
  };

  const fetchIssueFixersData = async () => {
    try {
      const apiResponse: ApiResponse = await dashboardPMSApis.fetchIssueFixersData(
        projectId,
        "topFiveIssueFixers"
      );
      const { data, status } = apiResponse;
      if (status && data) {
        setIssueFixersData(data);
      }
      setLoadingIssueFixers(false);
    } catch (error) {
      console.log(error);
      setLoadingIssueFixers(false);
    }
  };

  const fetchTaskSummary = async () => {
    try {
      const apiResponse: ApiResponse = await dashboardPMSApis.fetchTaskSummaryDashBoard(
        projectId,
        "taskSummaryDashBoard"
      );
      const { data, status } = apiResponse;
      if (status && data) {
        const displayLabelMapping: { [label: string]: string } = {
          InProgress: "In Progress",
          OnTrack: "On Track",
          OnHold: "On Hold",
          UnderQualityChecks: "Under Quality Check",
          OverdueTasks: "Overdue Tasks",
          OpenIssues: "Open Issues",
          ResolvedIssues: "Resolved Issues",
        };
        const enrichedSummaryData = data.map((item: any) => ({
          ...item,
          displayLabel: displayLabelMapping[item.label] || item.label,
          // statusColors is the single source of truth for status hexes
          color: statusTone(item.label).dot,
          textColor: statusTone(item.label).text,
        }));
        setSummaryData(enrichedSummaryData);
      }
      setLoadingSummary(false);
    } catch (error) {
      console.log(error);
      setLoadingSummary(false);
    }
  };

  const fetchPhasesDropDownData = async () => {
    if (!projectId) {
      setDropdownData([]);
      setSelectedPhase(null);
      return;
    }
    try {
      const apiResponse: ApiResponse = await dashboardPMSApis.fetchPhaseDropdown(projectId);
      const { data, status } = apiResponse;
      if (status && data) {
        setDropdownData(data);
        setSelectedPhase({
          phaseId: data[0]?.phaseId,
          phaseName: data[0]?.phaseName,
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchPhasesByStatus = async () => {
    try {
      const apiResponse: ApiResponse = await dashboardPMSApis.fetchPhaseLevelStatusDashBoard(
        projectId,
        "phaseLevelStatus",
        selectedPhase?.phaseId || null
      );
      const { data, status } = apiResponse;
      if (status && data) {
        setPhaseStatusData(data);
      }
      setLoadingPhaseStatus(false);
    } catch (error) {
      console.log(error);
      setLoadingPhaseStatus(false);
    }
  };

  useEffect(() => {
    if (projectId) {
      fetchPlannedDeviationData();
      fetchProjectCompletionStatus();
      fetchOverDueTaskOnGoing();
      fetchTaskProgress();
      fetchPlannedVsActualDashBoard();
      fetchIssueStatusDashBoard();
      fetchTopFiveGoGettersOrAssgigned();
      fetchIssueFixersData();
      fetchTaskSummary();
      fetchPhasesDropDownData();
    }
  }, [projectId]);

  useEffect(() => {
    if (selectedPhase?.phaseId) {
      fetchPhasesByStatus();
    }
  }, [selectedPhase?.phaseId]);

  const handleMenuClick = (e: any) => {
    const selected = dropdownData.find((item) => String(item.phaseId) === e.key);
    if (!selected) return;
    // Re-picking the same phase would set loading=true but the useEffect on
    // [selectedPhase?.phaseId] wouldn't re-run, leaving the chart stuck in
    // the spinner forever. Bail out — data is already current.
    if (selected.phaseId === selectedPhase?.phaseId) return;
    setSelectedPhase({
      phaseId: selected.phaseId,
      phaseName: selected.phaseName,
    });
    setLoadingPhaseStatus(true);
  };

  const menu = (
    <Menu onClick={handleMenuClick}>
      {dropdownData?.map((item: any) => (
        <Menu.Item key={String(item.phaseId)}>
          {wbsName(item.phaseName)}
        </Menu.Item>
      ))}
    </Menu>
  );

  // Tab content components
  const OverviewTab = () => (
    <Row gutter={[24, 24]}>
      <Col xs={24} lg={16}>
        <Card
          title="Planned vs Deviated Tasks"
          style={{ height: "100%" }}
          styles={{ body: { height: 320, padding: "12px 24px" } }}
        >
          <div style={{ height: "100%", width: "100%" }}>
            <PlannedVsDeviationChart
              key={`deviation-${activeTab}`}
              loading={loadingDeviationChart}
              chartData={plannedDeviationData}
            />
          </div>
        </Card>
      </Col>
      <Col xs={24} lg={8}>
        <Card
          title="Project Completion"
          style={{ height: "100%" }}
          styles={{ body: { height: 320, display: "flex", alignItems: "center", justifyContent: "center", padding: "12px 24px" } }}
        >
          <div style={{ width: "100%", height: "100%" }}>
            <ProjectCompletionDonutChart
              key={`completion-${activeTab}`}
              loading={loadingCompletionChart}
              chartData={completionChartData}
            />
          </div>
        </Card>
      </Col>
      <Col xs={24} lg={12}>
        <Card
          title={
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>Overdue Tasks</span>
              <GenericStatusPill label="Ongoing" tone={statusTone("Overdue")} />
            </div>
          }
        >
          <OverdueTasksBarChart
            key={`overdue-${activeTab}`}
            loading={loadingOverdueChart}
            chartData={overdueChartData}
          />
        </Card>
      </Col>
      <Col xs={24} lg={12}>
        {/* WeeklyDigestChart has its own Card wrapper */}
        <WeeklyDigestChart key={`weekly-${activeTab}`} id={projectId} />
      </Col>
    </Row>
  );

  const taskSummaryLabels = [
    "Open",
    "InProgress",
    "OnHold",
    "OnTrack",
    "Deviated",
    "UnderQualityChecks",
    "Closed",
    // friendly variants in case the API returns spaced names
    "On Track",
    "On Hold",
    "Under Quality Check",
    "In Progress",
    "Yet to Start",
    "Possible Delay",
    "Completed",
  ];
  const taskSummaryRows = (summaryData || []).filter((item: any) =>
    taskSummaryLabels.includes(item.label),
  );

  const TasksTab = () => (
    <Row gutter={[24, 24]}>
      <Col xs={24} lg={8}>
        <Card title="Task Summary" style={{ height: "100%" }}>
          {taskSummaryRows && taskSummaryRows.length > 0 ? (
            taskSummaryRows.map((item: any, index: number) => (
              <div
                key={index}
                onClick={() => goToTasksWithStatusFilter(item.label)}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "10px 8px",
                  borderRadius: 4,
                  borderBottom:
                    index < taskSummaryRows.length - 1
                      ? "1px solid #f0f0f0"
                      : "none",
                  cursor: "pointer",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLDivElement).style.background =
                    "#fafafa";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLDivElement).style.background =
                    "transparent";
                }}
                title={`View ${item.displayLabel || item.label} tasks`}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: item.color,
                    }}
                  />
                  <Text>{item.displayLabel || item.label}</Text>
                </div>
                <Text strong style={{ color: item.textColor, fontSize: 18 }}>
                  {item.count}
                </Text>
              </div>
            ))
          ) : (
            <Empty description="No data" />
          )}
        </Card>
      </Col>
      <Col xs={24} lg={16}>
        <Row gutter={[24, 24]}>
          <Col xs={24}>
            <Card title="Task Progress" styles={{ body: { height: 300 } }}>
              <div style={{ height: "100%", width: "100%" }}>
                <TaskProgressBarChart
                  key={`progress-${activeTab}`}
                  loading={loadingTaskProgressChart}
                  chartData={taskProgressChartData}
                />
              </div>
            </Card>
          </Col>
          <Col xs={24}>
            <Card title="Planned vs Actual Hours" styles={{ body: { height: 300 } }}>
              <div style={{ height: "100%", width: "100%" }}>
                <TaskPlannedVsActualChart
                  key={`plannedactual-${activeTab}`}
                  loading={loadingPlannedActual}
                  chartData={taskPlannedActualData}
                />
              </div>
            </Card>
          </Col>
        </Row>
      </Col>
    </Row>
  );

  const TeamTab = () => (
    <Row gutter={[24, 24]}>
      <Col xs={24} lg={16}>
        <Row gutter={[24, 24]}>
          <Col xs={24}>
            <Card
              title="Team Status — Tasks"
              styles={{ body: { padding: 0 } }}
            >
              <TeamStatusTable
                key={`team-status-${activeTab}`}
                id={projectId}
              />
            </Card>
          </Col>
          <Col xs={24}>
            <Card
              title="Team Status — Issues"
              styles={{ body: { padding: 0 } }}
            >
              <IssueTeamStatusTable
                key={`issue-team-status-${activeTab}`}
                id={projectId}
              />
            </Card>
          </Col>
        </Row>
      </Col>
      <Col xs={24} lg={8}>
        <Row gutter={[24, 24]}>
          <Col xs={24}>
            <Card title="Top 5 Go Getters" style={{ minHeight: 280 }}>
              <TopGoGetters key={`go-getters-${activeTab}`} loading={loadingGoGetters} data={goGettersData} />
            </Card>
          </Col>
          <Col xs={24}>
            <Card title="Top 5 Issue Fixers" style={{ minHeight: 280 }}>
              <TopIssueFixers key={`issue-fixers-${activeTab}`} loading={loadingIssueFixers} data={issueFixersData} />
            </Card>
          </Col>
        </Row>
      </Col>
    </Row>
  );

  const IssuesTab = () => (
    <Row gutter={[24, 24]}>
      <Col xs={24} lg={12}>
        <Card title="Issue Status" style={{ minHeight: 400 }}>
          <IssueStatusChart
            key={`issue-status-${activeTab}`}
            loading={loadingIssueStatus}
            issueData={issueStatusData}
          />
        </Card>
      </Col>
      <Col xs={24} lg={12}>
        <Card title="Overdue Work Items" style={{ minHeight: 400 }}>
          <OverdueWorkItems key={`overdue-items-${activeTab}`} id={projectId} />
        </Card>
      </Col>
    </Row>
  );

  const PhasesTab = () => (
    <Row gutter={[24, 24]}>
      <Col xs={24}>
        <Card
          title={
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>Tasks Status - Phase wise</span>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <GenericStatusPill label="Ongoing" tone={statusTone("Open")} />
                <Text style={{ fontSize: 13 }}>Select Phase:</Text>
                <Dropdown overlay={menu} trigger={["click"]}>
                  <a
                    onClick={(e) => e.preventDefault()}
                    style={{
                      fontSize: 12,
                      padding: "4px 12px",
                      border: "1px solid #d9d9d9",
                      borderRadius: 6,
                      display: "inline-flex",
                      alignItems: "center",
                      background: "#fff",
                    }}
                  >
                    {wbsName(selectedPhase?.phaseName) || "Select Phase"}{" "}
                    <DownOutlined style={{ fontSize: 10, marginLeft: 6 }} />
                  </a>
                </Dropdown>
              </div>
            </div>
          }
        >
          <PhaseStatusChart
            key={`phase-status-${activeTab}-${selectedPhase?.phaseId}`}
            loading={loadingPhaseStatus}
            data={phaseStatusData}
          />
        </Card>
      </Col>
    </Row>
  );

  // IMPORTANT: invoke the *Tab components as functions, not as JSX (`<X />`).
  // They are defined inline inside this component, so every parent render
  // creates a new function reference. Using `<OverviewTab />` causes Antd
  // Tabs to see a "new component type" each render and remount the entire
  // tab body — which re-fires fetches in children like WeeklyDigestChart
  // (visible as duplicate `weeklyDigestDashBoard` requests in the network
  // panel). Inlining the returned JSX keeps child element identity stable.
  const tabItems = [
    { key: "overview", label: "Overview", children: OverviewTab() },
    { key: "tasks", label: "Tasks", children: TasksTab() },
    { key: "team", label: "Team", children: TeamTab() },
    { key: "issues", label: "Issues", children: IssuesTab() },
    { key: "phases", label: "Phases", children: PhasesTab() },
  ];

  return (
    <React.Fragment>
      <Head title="Task Insights" />
      <Content>
        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <Title level={4} style={{ margin: 0 }}>
            {projectName || "Project"} — Task Insights
          </Title>
          <Text type="secondary">Overview of project performance and metrics</Text>
        </div>

        {/* KPI Cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} style={{ flex: "1 1 200px", maxWidth: "100%" }}>
            <StatCard
              label="Total Tasks"
              value={kpiData.totalTasks}
              icon={<ProjectOutlined />}
              tone="brand"
              loading={loadingSummary}
              onClick={() => {
                if (!projectId) return;
                navigate(`/pms/project/tasks/${projectId}`, {
                  state: {
                    ...(location.state || {}),
                    id: projectId,
                    projectName,
                    fromDashboard: true,
                  },
                });
              }}
            />
          </Col>
          <Col xs={24} sm={12} style={{ flex: "1 1 200px", maxWidth: "100%" }}>
            <StatCard
              label="Completed"
              value={kpiData.completed}
              icon={<CheckCircleOutlined />}
              tone="Completed"
              loading={loadingSummary}
              onClick={() => goToTasksWithStatusFilter("Closed")}
            />
          </Col>
          <Col xs={24} sm={12} style={{ flex: "1 1 200px", maxWidth: "100%" }}>
            <StatCard
              label="In Progress"
              value={kpiData.inProgress}
              icon={<ClockCircleOutlined />}
              tone="In Progress"
              loading={loadingSummary}
              onClick={() => goToTasksWithStatusFilter("InProgress")}
            />
          </Col>
          <Col xs={24} sm={12} style={{ flex: "1 1 200px", maxWidth: "100%" }}>
            <StatCard
              label="Overdue"
              value={kpiData.overdue}
              icon={<WarningOutlined />}
              tone="Overdue"
              loading={loadingSummary}
              onClick={() => goToTasksWithStatusFilter("Overdue")}
            />
          </Col>
          <Col xs={24} sm={12} style={{ flex: "1 1 200px", maxWidth: "100%" }}>
            <StatCard
              label="Total Issues"
              value={kpiData.issues}
              icon={<BugOutlined />}
              tone="On Hold"
              loading={loadingIssueStatus}
              onClick={() => {
                if (!projectId) return;
                navigate(`/pms/project/issues/${projectId}`, {
                  state: {
                    ...(location.state || {}),
                    id: projectId,
                    projectName,
                    fromDashboard: true,
                  },
                });
              }}
            />
          </Col>
        </Row>

        {/* Tabbed Content */}
        <Card
          style={{
            borderRadius: 12,
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          }}
          styles={{ body: { padding: "0 24px 24px" } }}
        >
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabItems}
            size="large"
            style={{ marginTop: 8 }}
            destroyInactiveTabPane
          />
        </Card>
      </Content>
    </React.Fragment>
  );
};

export default ProjectScheduleDashboard;
